import React, { useMemo } from 'react';
import { useContext } from 'react';
import { Box, Button, Paper, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { UiContext } from 'shared/lib/contexts/uiContext';
import IconEdit from 'shared/icons/iconEdit';
import { borderRadiusValues } from 'shared/lib/utils/uiConstants';
import { ProfileModel } from 'entities/profile/model/userModel';
import { useBooleanScreens } from 'shared/hooks/useBooleanScreens';
import ActionInlineButton from 'shared/lib/components/controls/buttons/actionInlineButton';
import { fixedSizes } from 'shared/hooks/useGetSpacing';

export default function ProfileCard({
  user,
  handleGoToProfileEdit,
}: {
  user: ProfileModel;
  handleGoToProfileEdit: () => void;
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const {
    spacings: {
      sp,
      profilePage: { profileCard },
    },
  } = useContext(UiContext);

  const { isLessLaptop, isLessTablet } = useBooleanScreens();

  const isTablet = isLessLaptop && !isLessTablet;

  const avatar = (
    <Box
      sx={{
        width: fixedSizes.profile.avatarStatic,
        height: fixedSizes.profile.avatarStatic,
        minWidth: fixedSizes.profile.avatarStatic,
        minHeight: fixedSizes.profile.avatarStatic,
      }}
    >
      <img
        src={user.avatar || user.avatarPlaceholder}
        style={{
          width: '100%',
          height: '100%',
          borderRadius: theme.spacing(borderRadiusValues.round),
          objectFit: 'cover',
        }}
        alt="avatar"
      ></img>
    </Box>
  );

  return (
    <Paper
      elevation={4}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        width: isLessLaptop ? 1 : profileCard.w,
        p: { mobile: 5, tablet: 7, laptop: sp.s150 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: isTablet ? 'row' : 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 6,
        }}
      >
        {avatar}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: isTablet ? 'start' : 'center',
            flexDirection: 'column',
            gap: sp.s30,
          }}
        >
          <Typography variant={isLessLaptop ? 'f20b' : 'f16b'}>
            {user.name}
          </Typography>
          <Typography variant="f16" color={'custom.textInactive'}>
            {user.email}
          </Typography>

          <ActionInlineButton
            sx={{ mt: sp.s40 }}
            Icon={IconEdit}
            sizeVariant="small"
            onClick={handleGoToProfileEdit}
          >
            {t('common.edit')}
          </ActionInlineButton>
        </Box>
      </Box>
    </Paper>
  );
}
